import { IHeaderGroupAngularComp } from '@ag-grid-community/angular';
import { IHeaderGroupParams } from '@ag-grid-community/core';
import { Component, Input, OnChanges } from '@angular/core';
import { createUniqueId, ToggleButton } from '@frk/eds-components';
import { CalcTypeConfig } from '@products/fund-listing/services/calc-type.config';
import { SiteConfigService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  CalcTypeStd,
  SwitchProps,
  SwitchState,
  ToggleProps,
  TogglePropsOption,
} from '@types';
import { Logger } from '@utils/logger';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FundListingCalcTypeController } from '../../controllers';

const logger = Logger.getLogger('CalcTypeGroupHeaderComponent');

export interface CalcTypeGroupHeaderParams
  extends FundListingCalcTypeController,
    IHeaderGroupParams {}

@Component({
  selector: 'ft-calc-type-group-header',
  templateUrl: './calc-type-group-header.component.html',
  styleUrls: ['./calc-type-group-header.component.scss'],
})
export class CalcTypeGroupHeaderComponent
  implements IHeaderGroupAngularComp, OnChanges {
  // this (using ngOnchanges) allows the switch to be used as a normal component in card view
  @Input() controller: FundListingCalcTypeController;
  public isAvailable = false;
  public isSwitchAvailable: boolean;
  public switchProps: SwitchProps;
  public switchState$: Observable<SwitchState>;
  private configurationId: string;
  public toggleButtons: ToggleButton[] = [];
  public label = '';
  private calcOptions: ToggleProps;
  public switchId = '';

  constructor(
    private translateService: TranslateService,
    private siteConfigService: SiteConfigService
  ) {}

  public ngOnChanges(): void {
    if (this.controller) {
      this.init();
    }
  }

  public agInit(params: CalcTypeGroupHeaderParams): void {
    logger.debug('agInit', params);
    this.controller = params;
    this.controller.setGridAPIs(params);
    this.init();
  }

  private init() {
    this.isAvailable = this.controller.showCalcTypeHeader;
    const calcTypeConfig: CalcTypeConfig = this.controller.getCalcTypeConfig();
    const selected: CalcTypeStd = this.controller.getSelectedCalcType();
    this.switchId = createUniqueId('calcSwitchProps_');

    if (typeof calcTypeConfig !== 'string') {
      const calcOptions = calcTypeConfig as ToggleProps;
      this.calcOptions = calcOptions;
      if (calcOptions.labelKey) {
        this.label = this.translateService.instant(calcOptions.labelKey);
      }
      if (calcOptions.type === 'toggle') {
        this.toggleButtons = []; // reset array, as ngOnChanges() can be called more than once
        calcOptions.options.forEach((option: TogglePropsOption) => {
          this.toggleButtons.push({
            active: option.value === selected,
            text: this.translateService.instant(option.label),
            value: option.value,
          });
        });
      } else if (
        calcOptions.type === 'switch' &&
        calcOptions.options.length > 1
      ) {
        this.switchProps = {
          labelKey: calcOptions.labelKey,
          labelOffKey: calcOptions.options[0].label,
          labelOnKey: calcOptions.options[1].label,
          state:
            calcOptions.options[0].value === selected
              ? SwitchState.OFF
              : SwitchState.ON,
        };
        this.switchState$ = this.controller
          .getCalcType$()
          .pipe(
            map(
              (calcType: CalcTypeStd): SwitchState =>
                calcOptions.options[0].value === calcType
                  ? SwitchState.OFF
                  : SwitchState.ON
            )
          );
      }
    }

    this.isSwitchAvailable = this.showSwitchProp();
  }

  private showSwitchProp(): boolean {
    return (
      this.switchProps &&
      (this.siteConfigService.isSiteInternational() ||
        this.configurationId !== 'MMF')
    );
  }

  public onSwitchPressed(state: SwitchState): void {
    if (this.controller.setCalcType) {
      const selected: CalcTypeStd =
        state === SwitchState.OFF
          ? this.calcOptions.options[0].value
          : this.calcOptions.options[1].value;
      this.controller.setCalcType(selected);
    }
  }

  public onToggleActive(selectedOption: ToggleButton<CalcTypeStd>): void {
    if (this.controller.setCalcType) {
      this.controller.setCalcType(selectedOption.value);
    }
  }
}
